import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  PAT_FINALIZED_SCORES_EMAIL_PREVIEW_QUERY,
  type EmailPreview,
  type PatFinalizedScoresEmailPreviewQueryResponse,
  type PatFinalizedScoresEmailPreviewQueryVariables
} from "../_queries/emailPreviewQueries";

export function usePatFinalizedScoresEmailPreview(eventId: string) {
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    PatFinalizedScoresEmailPreviewQueryResponse,
    PatFinalizedScoresEmailPreviewQueryVariables
  >(PAT_FINALIZED_SCORES_EMAIL_PREVIEW_QUERY, { fetchPolicy: "network-only" });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(async () => {
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;

    setLoading(true);
    setPreview(null);
    setError(null);

    const result = await fetchPreview({
      variables: { eventId }
    });

    if (requestId !== activeRequestIdRef.current) {
      return result;
    }

    if (result.error) {
      setError(result.error);
      setPreview(null);
    } else {
      setPreview(result.data?.patFinalizedScoresEmailPreview ?? null);
      setError(null);
    }

    setLoading(false);
    return result;
  }, [eventId, fetchPreview]);

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
